import { get, isFinite, mapValues, keys, chain, find } from "lodash"
import { createSelector } from "reselect"
import { getSelectedExperiment } from "."
import { getPortfolioEquity } from "./data"
import { getAssetClassFromMic, assetClassKeyMapIteratee } from "helpers/securities"
import { getPortfolioLmv, getPortfolioSmv } from "./market-values"
import { getForecasts } from "modules/portfolio-root/selectors/forecast"

const safeValue = (value, divide = true) => {
  const floatValue = parseFloat(value)
  if (isFinite(floatValue)) {
    return divide ? floatValue / 100 : floatValue
  } else {
    return undefined
  }
}

const CONSTRAINTS = {
  tradeSwaps: constraints => {
    return !!constraints.tradeSwaps
  },
  maxTurnover: constraints => {
    const floatValue = parseFloat(constraints.maxTurnover)
    if (isFinite(floatValue)) {
      return floatValue / 100
    } else {
      return 1
    }
  },
  maxConcentration: constraints => safeValue(constraints.maxConcentration),
  maxTrade: constraints => chain(constraints)
    .get("maxTrade")
    .mapValues((value) => safeValue(value, false))
    .value(),
  longMarketValue: (constraints, { portfolioLmv, portfolioSmv }) => {
    const netLong = parseFloat(constraints.netLong)
    let equityChange = parseFloat(constraints.equityChange)

    if (!isFinite(equityChange) && !isFinite(netLong)) {
      return undefined
    }

    if (isFinite(equityChange)) {
      equityChange /= 100
    } else {
      equityChange = 0
    }

    let lmv = portfolioLmv
    if (isFinite(netLong)) {
      lmv = (portfolioLmv + portfolioSmv) * netLong / 100
    }

    return lmv * (1 + equityChange)
  },
  shortMarketValue: (constraints, { portfolioLmv, portfolioSmv }) => {
    const netLong = parseFloat(constraints.netLong)
    let equityChange = parseFloat(constraints.equityChange)

    if (!isFinite(equityChange) && !isFinite(netLong)) {
      return undefined
    }

    if (isFinite(equityChange)) {
      equityChange /= 100
    } else {
      equityChange = 0
    }

    let smv = portfolioSmv
    if (isFinite(netLong)) {
      smv = (portfolioLmv + portfolioSmv) * (1 - netLong / 100)
    }

    return smv * (1 + equityChange)
  },
}

function getSecurityConstraints(securityConstraints, portfolioEquity) {
  if (!keys(securityConstraints).length) {
    return
  }

  return chain(securityConstraints)
    .groupBy(security => getAssetClassFromMic(security.mic))
    .mapKeys(assetClassKeyMapIteratee)
    .mapValues((securities) =>
      chain(securities)
        .map(({ ticker, mic, originalPercentEquity, constraintType, min, max }) => {
          let id, minPercentEquity, maxPercentEquity
          if (constraintType === "range") {
            minPercentEquity = originalPercentEquity + parseFloat(min) / 100
            maxPercentEquity = originalPercentEquity + parseFloat(max) / 100
          } else {
            minPercentEquity = originalPercentEquity
            maxPercentEquity = originalPercentEquity
          }

          switch (getAssetClassFromMic(mic)) {
            case "equity":
              id = { ticker, mic }
              break
            default:
              id = ticker
              break
          }

          return {
            id,
            minEconomicExposure: minPercentEquity * portfolioEquity,
            maxEconomicExposure: maxPercentEquity * portfolioEquity,
          }
        })
        .filter(position => position)
        .value(),
    )
    .value()
}

export const getExperimentSecuritiesConstraints = createSelector(
  [getSelectedExperiment],
  (experiment) => {
    if (!experiment) {
      return null
    }

    if (experiment.type === "OPTIMIZATION" && experiment.config) {
      return get(experiment, "config.constraints.securities", {})
    }

    return {}
  })

export const getCurrentConstraints = state => get(state, "modules.optimization.constraints")
export const getCurrentSettings = state => get(state, "modules.optimization.constraints.settings")
export const getCurrentHorizon = state => get(state, "modules.optimization.constraints.settings.horizon")
export const getActivatedConstraints = state => get(state, "modules.optimization.activatedConstraints")
export const getForecastConstraint = state => get(state, "modules.optimization.constraints.settings.forecast")

// Check the selected forecast (from user preferences) against forecasts
export const getSelectedCustomForecast = createSelector(
  [getForecasts, getForecastConstraint],
  (forecasts, forecastConstraint) =>
    find(forecasts, ({ id }) => id === forecastConstraint)
      ? forecastConstraint
      : null,
)

export const getApiConstraints = createSelector(
  [getCurrentConstraints, getPortfolioLmv, getPortfolioSmv, getPortfolioEquity],
  (constraints, portfolioLmv, portfolioSmv, portfolioEquity) => ({
    ...mapValues(CONSTRAINTS, fn => {
      return fn({ ...constraints.general }, { portfolioLmv, portfolioSmv })
    }),
    exposure: constraints.exposure,
    securities: getSecurityConstraints(constraints.securities, portfolioEquity),
  }),
)
